/**
 * One newest-first notification list over several apps, a page at a time.
 *
 * The API pages each app on its own — there is no cross-tenant endpoint, by
 * design — so in the all-apps scope the pages have to be merged here. Merging
 * whole pages side by side gets the order wrong: app A's twentieth item can be
 * a week older than app B's twenty-first. So every app keeps a buffer, and an
 * item is only handed out once no app that still has more could be hiding
 * something newer: every such app must have at least one item buffered.
 *
 * @param {object} options
 * @param {string[]} options.appIds
 * @param {(appId: string, cursor: string|null) => Promise<{data: object[], meta: object}>} options.fetchPage
 * @param {number} [options.pageSize]
 * @param {(item: object) => string|Date} [options.timeOf] What the list is
 *   ordered by. Notifications use `at`; the inbox reuses this for conversations,
 *   which are ordered by `lastMessageAt`.
 */
export function createNotificationFeed({ appIds, fetchPage, pageSize = 20, timeOf = (item) => item.at }) {
  const sources = appIds.map((appId) => ({ appId, cursor: null, hasMore: true, buffer: [] }));

  const time = (item) => new Date(timeOf(item)).getTime();

  async function refill() {
    const empty = sources.filter((s) => s.hasMore && s.buffer.length === 0);
    await Promise.all(
      empty.map(async (source) => {
        try {
          const { data, meta } = await fetchPage(source.appId, source.cursor);
          source.buffer.push(...(Array.isArray(data) ? data : []));
          source.cursor = meta?.nextCursor ?? null;
          source.hasMore = Boolean(meta?.hasMore && source.cursor);
        } catch {
          // One app failing drops out of the merge rather than emptying it.
          source.hasMore = false;
        }
      })
    );
  }

  return {
    /** The next page, and whether anything is left after it. */
    async next() {
      const items = [];
      while (items.length < pageSize) {
        await refill();
        const ready = sources.filter((s) => s.buffer.length > 0);
        if (ready.length === 0) break;

        const newest = ready.reduce((a, b) => (time(b.buffer[0]) > time(a.buffer[0]) ? b : a));
        items.push(newest.buffer.shift());
      }
      const hasMore = sources.some((s) => s.hasMore || s.buffer.length > 0);
      return { items, hasMore };
    },
  };
}

export default createNotificationFeed;
